import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from 'classnames';
import { useContextData, usePageParams } from 'src/hooks';
import { checkIsLaptop } from 'src/helper';
import { Auth, BackButton, BreadCrumbs } from 'src/components';
import { LangSelect } from 'src/components/general';
import styles from './headerStyle.module.scss';

export const Header = () => {
  const {
    header: { setSize },
    screen: { setIsLaptop },
    navigator: { backUrl: customBackUrl, setBackUrl },
  } = useContextData();
  const { breadCrumbs, header, backUrl: defaultBackUrl } = usePageParams();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  const backUrl = useMemo(
    () => customBackUrl ?? defaultBackUrl,
    [defaultBackUrl, customBackUrl],
  );

  useEffect(() => {
    if (!ref.current) {
      return () => undefined;
    }

    let timerId: ReturnType<typeof setTimeout>;

    const observer = new ResizeObserver(() => {
      clearTimeout(timerId);

      timerId = setTimeout(() => {
        const rect = ref?.current?.getBoundingClientRect();
        if (rect) {
          setSize({ width: rect.width, height: rect.height });
        }
        setIsLaptop(checkIsLaptop());
      }, 30);
    });
    observer.observe(ref.current);

    return () => {
      clearTimeout(timerId);
      observer.disconnect();
    };
  }, [setIsLaptop, setSize]);

  const hasBreadCrumbs = useMemo(
    () => breadCrumbs && breadCrumbs.some((bc) => bc !== null),
    [breadCrumbs],
  );

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.leftSection}>
        <div
          className={cl(
            styles.backButtonSection,
            !backUrl && styles.withoutBackButton,
          )}
        >
          {backUrl && (
            <BackButton
              onClick={() => {
                setBackUrl(null);
                navigate(backUrl);
              }}
            />
          )}
        </div>
        <div className={styles.labelSection}>
          <div className={styles.labelText}>{header}</div>
          {hasBreadCrumbs && <BreadCrumbs items={breadCrumbs} />}
        </div>
      </div>
      <div className={styles.authSection}>
        <Auth />
        <LangSelect className={styles.lang} />
      </div>
    </div>
  );
};

export default Header;
